import { useCallback, useEffect, useRef, useState } from "react";
import L from "leaflet";
import { Search } from "lucide-react";
import { Input } from "@/components/ui/input";
import { fieldInputCls } from "@/components/Field";
import { useT } from "@/lib/i18n";
import { useLeafletMap, useMapClick } from "@/lib/useLeafletMap";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";

export const LocationPicker = ({ lat, lon, onChange }) => {
  const { t } = useT();
  const has = lat != null && lon != null;
  const [ref, map] = useLeafletMap(has ? [lat, lon] : [44.5, 10.5], has ? 16 : 5);
  const marker = useRef(null);
  const [q, setQ] = useState("");
  const pick = useCallback((ll) => onChange(Number(ll.lat.toFixed(6)), Number(ll.lng.toFixed(6))), [onChange]);
  useMapClick(map, true, pick);

  useEffect(() => {
    if (!map) {
      return;
    }
    marker.current?.remove();
    if (lat != null && lon != null) {
      marker.current = L.circleMarker([lat, lon], { radius: 8, color: "#fff", weight: 2, fillColor: "#FF3B30", fillOpacity: 1 }).addTo(map);
      if (!map.getBounds().contains([lat, lon])) map.setView([lat, lon], 16);
    }
  }, [map, lat, lon]);

  const search = async () => {
    if (!q.trim() || !map) return;
    try {
      const { data } = await api.get("/geocode", { params: { q } });
      const hit = data[0];
      if (hit) map.setView([hit.lat, hit.lon], 16);
      else toast.error(t("no_results"));
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  return (
    <div data-testid="location-picker" className="space-y-2">
      <div className="flex gap-2">
        <Input data-testid="location-search-input" placeholder={t("search_place")} value={q} onChange={(e) => setQ(e.target.value)}
          onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); search(); } }} className={fieldInputCls} />
        <button type="button" data-testid="location-search-btn" onClick={search} className="px-3 border border-[#282D3D] rounded-sm text-slate-300 hover:text-white"><Search className="w-4 h-4" /></button>
      </div>
      <div className="relative z-0"><div ref={ref} style={{ height: 240 }} className="w-full rounded-sm" /></div>
      <p className="font-mono text-[10px] text-slate-500">{t("click_to_set_center")}</p>
    </div>
  );
};
